/**
 * FG-1 (D-TBD): upload instance data only when it changes.
 *
 * three r186's `Attributes.update` re-uploads a `DynamicDrawUsage` attribute on every render call that draws it, whether
 * or not its version moved (D-982 measured ~0.5 MB a frame of unchanged field-rock and light-pool instance data). A
 * `StaticDrawUsage` attribute uploads only when its version moves, i.e. after `needsUpdate = true`. The usage has no
 * other effect on the WebGPU path (buffer flags and shaders are the same), so the image is unchanged as long as every
 * write is flagged. `uploadOnChange: false` (uploads.json) restores the old every-call upload.
 *
 * Small `InstancedMesh`es are the other half: three r186 (`nodes/accessors/Instance.js`) puts the instance matrices of
 * a mesh whose `capacity × 64 B` fits the device's uniform-buffer limit into a `buffer()` uniform in the object group,
 * and that group is re-sent on every draw whatever the version. A larger capacity takes the interleaved-attribute
 * path, which is version-gated like the attributes above. `attributePathCapacity` gives a bucket that minimum (one
 * matrix past the limit read from the backend: 1025 at WebGPU's default 64 KiB); the draw still covers only `count`.
 * A storage attribute would do the same, but vertex-stage storage buffers can be absent on compatibility-mode devices
 * and the WebGL2 fallback, so the attribute path is the safe one.
 */
import { DynamicDrawUsage, StaticDrawUsage } from 'three/webgpu'
import { z } from 'zod'
import uploadsJson from './uploads.json'

export const uploadsSchema = z.object({ uploadOnChange: z.boolean() }).strict()
export type UploadsConfig = z.infer<typeof uploadsSchema>

const UPLOADS: UploadsConfig = uploadsSchema.parse(uploadsJson)

/** The usage for instance data whose every write sets `needsUpdate` (the caller's contract). */
export function flaggedUsage(cfg: UploadsConfig = UPLOADS): typeof StaticDrawUsage | typeof DynamicDrawUsage {
  return cfg.uploadOnChange ? StaticDrawUsage : DynamicDrawUsage
}

/** WebGPU's default `maxUniformBufferBindingSize` (and the usual WebGL2 `MAX_UNIFORM_BLOCK_SIZE`), when unreadable. */
export const DEFAULT_UNIFORM_BUFFER_LIMIT = 65536
const MAT4_BYTES = 64

type LimitSource = { readonly backend?: { readonly capabilities?: { getUniformBufferLimit?: () => number } } } | null | undefined

/** The renderer backend's uniform-buffer byte limit (three's own `getUniformBufferLimit`), or the default before the device is up. */
export function uniformBufferLimit(renderer: unknown): number {
  try {
    const n = (renderer as LimitSource)?.backend?.capabilities?.getUniformBufferLimit?.()
    if (typeof n === 'number' && n > 0) return n
  } catch {
    // the WebGPU device isn't created yet: fall through to the default
  }
  return DEFAULT_UNIFORM_BUFFER_LIMIT
}

/**
 * The instance capacity that keeps an `InstancedMesh` on three's version-gated attribute path: `capacity`, raised to one
 * matrix past `limitBytes` when the switch is on (the switch off keeps the old capacity and the uniform path).
 */
export function attributePathCapacity(capacity: number, limitBytes: number, cfg: UploadsConfig = UPLOADS): number {
  if (!cfg.uploadOnChange) return capacity
  return Math.max(capacity, Math.floor(limitBytes / MAT4_BYTES) + 1)
}
